import { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { TaskForm, type TaskFormState } from './TaskForm'
import { sheetFetch } from '@/lib/sheetFetch'
import { TeamPicker, useTeamMemberships, defaultTeamId } from '@/components/TeamPicker'
import type { AssigneeOption, TaskDetail, TaskSubject } from '@/types/work'

// Inner content for the create flow. Used by CreateTaskSheet (the
// standalone "Nouvelle tâche" sheet on /work/tasks) AND by
// TaskDetailSheet's create-subtask mode (where it renders inline,
// without its own Sheet wrapper).
//
// When `parentTaskId` is provided, the form's POST includes
// parent_task_id so the new task is persisted as a subtask. The
// controller's task_params allowlist permits parent_task_id only on
// #create — UpdateTask still rejects it on PATCH.
//
// On success: invokes onCreated(taskId) and reloads the index list.
// onCancel returns to the surrounding context (closes the sheet, or
// flips back to detail mode in TaskDetailSheet).

interface NewPayload {
  task: TaskDetail
  assignee_options: AssigneeOption[]
  prefill: TaskSubject | null
}

interface CreateTaskSheetContentProps {
  onCreated: (taskId: number) => void
  onCancel: () => void
  parentTaskId?: number
  // When set, shown as a subtitle ("Sous-tâche de: <title>") so the
  // user has context inside an embedded subtask-create flow.
  parentTaskTitle?: string
}

export function CreateTaskSheetContent({
  onCreated,
  onCancel,
  parentTaskId,
  parentTaskTitle,
}: CreateTaskSheetContentProps) {
  const [data, setData] = useState<NewPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [state, setState] = useState<TaskFormState | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [processing, setProcessing] = useState(false)

  // Owning-team choice (US3). Read from the page's Inertia shared props,
  // NOT from the JSON /work/tasks/new payload (which omits auth.user). The
  // picker hides itself for single-team users; multi-team users default to
  // their primary team.
  const memberships = useTeamMemberships()
  const multiTeam = memberships.length > 1
  const [teamId, setTeamId] = useState<string>(() => defaultTeamId(memberships))

  useEffect(() => {
    setLoading(true)
    const controller = new AbortController()
    fetch('/work/tasks/new', {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<NewPayload>
      })
      .then((payload) => {
        setData(payload)
        setState({
          title: payload.task.title ?? '',
          description: payload.task.description ?? '',
          priority: payload.task.priority,
          task_type: payload.task.task_type,
          due_at: payload.task.due_at ?? '',
          assignee_user_id: payload.task.assignee_user_id ?? null,
          subject: payload.prefill ?? payload.task.subject ?? null,
        })
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error(err)
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [])

  function update<K extends keyof TaskFormState>(key: K, value: TaskFormState[K]) {
    setState((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!state) return
    setProcessing(true)
    setErrors({})
    try {
      const taskPayload: Record<string, unknown> = {
        title: state.title,
        description: state.description,
        priority: state.priority,
        task_type: state.task_type,
        due_at: state.due_at || null,
        assignee_user_id: state.assignee_user_id,
        subject_type: state.subject?.type ?? null,
        subject_id: state.subject?.id ?? null,
      }
      if (parentTaskId) taskPayload.parent_task_id = parentTaskId

      const body: Record<string, unknown> = { source: 'sheet', task: taskPayload }
      // Only send team_id when the picker is shown (multi-team); otherwise the
      // backend files under the actor's primary team. team_id is a top-level
      // sibling param, not nested in `task`.
      if (multiTeam) body.team_id = teamId

      const response = await sheetFetch('/work/tasks', {
        method: 'POST',
        body,
      })
      if (response.status === 201) {
        const json = (await response.json()) as { task: { id: number } }
        onCreated(json.task.id)
        router.reload({ only: [ 'tasks', 'pagy', 'filters' ] })
      } else if (response.status === 422) {
        const json = (await response.json()) as { errors: Record<string, string> }
        setErrors(json.errors ?? {})
      } else {
        setErrors({ base: 'Une erreur est survenue.' })
      }
    } catch {
      setErrors({ base: 'Impossible de créer la tâche.' })
    } finally {
      setProcessing(false)
    }
  }

  if (loading || !state || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  const heading = parentTaskId ? 'Nouvelle sous-tâche' : 'Nouvelle tâche'

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">{heading}</h2>
        {parentTaskTitle && (
          <p className="text-sm text-muted-foreground">
            Sous-tâche de : {parentTaskTitle}
          </p>
        )}
      </div>

      {errors.base && <p className="text-sm text-destructive">{errors.base}</p>}

      {/* Owning team (multi-team users only). The picker hides itself for
          single-team users; in that case no team_id is sent and the backend
          files the task under the actor's primary team. */}
      <TeamPicker value={teamId} onChange={setTeamId} error={errors.team_id} />

      <TaskForm
        state={state}
        onChange={update}
        onSubmit={submit}
        errors={errors}
        processing={processing}
        assigneeOptions={data.assignee_options}
        actions={
          <>
            <Button type="button" variant="ghost" onClick={onCancel} disabled={processing}>
              Annuler
            </Button>
            <Button type="submit" disabled={processing}>
              Créer
            </Button>
          </>
        }
      />
    </div>
  )
}
